import { useState } from 'react';
import {
  ChevronDown, BookOpen, Calculator, Globe, Zap,
  FlaskConical, Leaf, Landmark, MapPin, Newspaper,
  Languages, Award, Target, Clock, FileText, Shield,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';

const NDA_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "NDA Syllabus 2026 – Complete Paper I & Paper II Topics",
  "description": "Complete NDA 2026 syllabus from official UPSC notification. Paper I Mathematics (300 marks) — Algebra, Trigonometry, Calculus, Vectors, Statistics. Paper II GAT (600 marks) — English, Physics, Chemistry, History, Geography, Current Events.",
  "author": { "@type": "Organization", "name": "EduPulseAI" },
  "publisher": { "@type": "Organization", "name": "EduPulseAI", "url": "https://edupulseai.co.in" },
  "datePublished": "2026-08-20",
  "dateModified": "2026-08-20",
  "mainEntityOfPage": { "@type": "WebPage", "@id": "https://edupulseai.co.in/syllabus/nda" },
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://edupulseai.co.in" },
      { "@type": "ListItem", "position": 2, "name": "Syllabus", "item": "https://edupulseai.co.in/syllabus/nda" },
      { "@type": "ListItem", "position": 3, "name": "NDA Syllabus 2026" }
    ]
  },
  "speakable": { "@type": "SpeakableSpecification", "cssSelector": ["h1", "h2"] }
};

/* ── Exam pattern data ─────────────────────────────────────────────────── */
const PAPERS = [
  { code: '01', name: 'Mathematics', duration: '2½ Hours', marks: 300, color: '#6366F1' },
  { code: '02', name: 'General Ability Test', duration: '2½ Hours', marks: 600, color: '#10B981' },
];

/* ── Paper I — Mathematics topics ─────────────────────────────────────── */
const MATH_TOPICS = [
  {
    id: 1, title: 'Algebra', color: '#6366F1',
    points: [
      'Concept of set, operations on sets, Venn diagrams, De Morgan laws, Cartesian product, relation, equivalence relation.',
      'Representation of real numbers on a line. Complex numbers — basic properties, modulus, argument, cube roots of unity.',
      'Binary system of numbers. Conversion of a number in decimal system to binary system and vice-versa.',
      'Arithmetic, Geometric and Harmonic progressions. Quadratic equations with real coefficients.',
      'Solution of linear inequations of two variables by graphs. Permutation and Combination.',
      'Binomial theorem and its applications. Logarithms and their applications.',
    ],
  },
  {
    id: 2, title: 'Matrices and Determinants', color: '#8B5CF6',
    points: [
      'Types of matrices, operations on matrices.',
      'Determinant of a matrix, basic properties of determinants.',
      'Adjoint and inverse of a square matrix.',
      'Applications — solution of a system of linear equations in two or three unknowns by Cramer\'s rule and by Matrix Method.',
    ],
  },
  {
    id: 3, title: 'Trigonometry', color: '#EC4899',
    points: [
      'Angles and their measures in degrees and in radians. Trigonometrical ratios.',
      'Trigonometric identities, Sum and difference formulae.',
      'Multiple and Sub-multiple angles. Inverse trigonometric functions.',
      'Applications — Height and distance, properties of triangles.',
    ],
  },
  {
    id: 4, title: 'Analytical Geometry of Two and Three Dimensions', color: '#F59E0B',
    points: [
      'Rectangular Cartesian Coordinate system. Distance formula. Equation of a line in various forms.',
      'Angle between two lines. Distance of a point from a line. Equation of a circle in standard and in general form.',
      'Standard forms of parabola, ellipse and hyperbola. Eccentricity and axis of a conic.',
      'Point in a three dimensional space, distance between two points. Direction Cosines and direction ratios.',
      'Equation two points. Direction Cosines and direction ratios. Equation of a plane and a line in various forms.',
      'Angle between two lines and angle between two planes. Equation of a sphere.',
    ],
  },
  {
    id: 5, title: 'Differential Calculus', color: '#14B8A6',
    points: [
      'Concept of a real valued function — domain, range and graph of a function. Composite functions, one to one, onto and inverse functions.',
      'Notion of limit, Standard limits — examples. Continuity of functions — examples, algebraic operations on continuous functions.',
      'Derivative of function at a point, geometrical and physical interpretation of a derivative — applications.',
      'Derivatives of sum, product and quotient of functions, derivative of a function with respect to another function, derivative of a composite function.',
      'Second order derivatives. Increasing and decreasing functions. Application of derivatives in problems of maxima and minima.',
    ],
  },
  {
    id: 6, title: 'Integral Calculus and Differential Equations', color: '#3B82F6',
    points: [
      'Integration as inverse of differentiation, integration by substitution and by parts.',
      'Standard integrals involving algebraic expressions, trigonometric, exponential and hyperbolic functions.',
      'Evaluation of definite integrals — determination of areas of plane regions bounded by curves — applications.',
      'Definition of order and degree of a differential equation, formation of a differential equation by examples.',
      'General and particular solution of a differential equations, solution of first order and first degree differential equations of various types — examples.',
      'Application in problems of growth and decay.',
    ],
  },
  {
    id: 7, title: 'Vector Algebra', color: '#F97316',
    points: [
      'Vectors in two and three dimensions, magnitude and direction of a vector.',
      'Unit and null vectors, addition of vectors, scalar multiplication of a vector.',
      'Scalar product or dot product of two vectors. Vector product or cross product of two vectors.',
      'Applications — work done by a force and moment of a force and in geometrical problems.',
    ],
  },
  {
    id: 8, title: 'Statistics and Probability', color: '#EF4444',
    points: [
      'Statistics: Classification of data, Frequency distribution, cumulative frequency distribution — examples.',
      'Graphical representation — Histogram, Pie Chart, frequency polygon — examples.',
      'Measures of Central tendency — Mean, median and mode. Variance and standard deviation — determination and comparison.',
      'Correlation and regression.',
      'Probability: Random experiment, outcomes and associated sample space, events, mutually exclusive and exhaustive events, impossible and certain events.',
      'Union and Intersection of events. Complementary, elementary and composite events.',
      'Classical and statistical definition of probability — examples. Elementary theorems on probability — simple problems.',
      'Conditional probability, Bayes\' theorem — simple problems.',
      'Random variable as function on a sample space. Binomial distribution, examples of random experiments giving rise to Binomial distribution.',
    ],
  },
];

/* ── Paper II — GAT sections ───────────────────────────────────────────── */
const GK_SECTIONS = [
  {
    id: 'A', name: 'Physics', weightage: 25, color: '#6366F1', Icon: Zap,
    points: [
      'Physical Properties and States of Matter, Mass, Weight, Volume, Density and Specific Gravity, Principle of Archimedes, Pressure Barometer.',
      'Motion of objects, Velocity and Acceleration, Newton\'s Laws of Motion, Force and Momentum, Parallelogram of Forces, Stability and Equilibrium of bodies, Gravitation, elementary ideas of work, Power and Energy.',
      'Effects of Heat, Measurement of Temperature and Heat, change of State and Latent Heat, Modes of transference of Heat.',
      'Sound waves and their properties, Simple musical instruments.',
      'Rectilinear propagation of Light, Reflection and refraction, Spherical mirrors and Lenses, Human Eye.',
      'Natural and Artificial Magnets, Properties of a Magnet, Earth as a Magnet.',
      'Static and Current Electricity, conductors and Non-conductors, Ohm\'s Law, Simple Electrical Circuits, Heating, Lighting and Magnetic effects of Current, Measurement of Electrical Power, Primary and Secondary Cells, Use of X-Rays.',
      'General Principles in the working of: Simple Pendulum, Pulleys, Siphon, Levers, Balloon, Pumps, Hydrometer, Pressure Cooker, Thermos Flask, Gramophone, Telegraphs, Telephone, Periscope, Telescope, Microscope, Mariner\'s Compass, Lightning Conductors, Safety Fuses.',
    ],
  },
  {
    id: 'B', name: 'Chemistry', weightage: 15, color: '#10B981', Icon: FlaskConical,
    points: [
      'Physical and Chemical changes. Elements, Mixtures and Compounds, Symbols, Formulae and simple Chemical Equations, Law of Chemical Combination (excluding problems). Properties of Air and Water.',
      'Preparation and Properties of Hydrogen, Oxygen, Nitrogen and Carbon dioxide, Oxidation and Reduction.',
      'Acids, bases and salts.',
      'Carbon — different forms.',
      'Fertilizers — Natural and Artificial.',
      'Material used in the preparation of substances like Soap, Glass, Ink, Paper, Cement, Paints, Safety Matches and Gun-Powder.',
      'Elementary ideas about the structure of Atom, Atomic Equivalent and Molecular Weights, Valency.',
    ],
  },
  {
    id: 'C', name: 'General Science', weightage: 10, color: '#22C55E', Icon: Leaf,
    points: [
      'Difference between the living and non-living.',
      'Basis of Life — Cells, Protoplasms and Tissues. Growth and Reproduction in Plants and Animals.',
      'Elementary knowledge of Human Body and its important organs.',
      'Common Epidemics, their causes and prevention.',
      'Food — Source of Energy for man. Constituents of food, Balanced Diet.',
      'The Solar System — Meteors and Comets, Eclipses.',
      'Achievements of Eminent Scientists.',
    ],
  },
  {
    id: 'D', name: 'History & Freedom Movement', weightage: 20, color: '#F59E0B', Icon: Landmark,
    points: [
      'A broad survey of Indian History, with emphasis on Culture and Civilisation.',
      'Freedom Movement in India.',
      'Elementary study of Indian Constitution and Administration.',
      'Elementary knowledge of Five Year Plans of India.',
      'Panchayati Raj, Co-operatives and Community Development.',
      'Bhoodan, Sarvodaya, National Integration and Welfare State, Basic Teachings of Mahatma Gandhi.',
      'Forces shaping the modern world: Renaissance, Exploration and Discovery, War of American Independence, French Revolution, Industrial Revolution and Russian Revolution.',
      'Impact of Science and Technology on Society. Concept of one World, United Nations, Panchsheel, Democracy, Socialism and Communism. Role of India in the present world.',
    ],
  },
  {
    id: 'E', name: 'Geography', weightage: 20, color: '#3B82F6', Icon: MapPin,
    points: [
      'The Earth, its shape and size. Latitudes and Longitudes, Concept of time. International Date Line. Movements of Earth and their effects.',
      'Origin of Earth. Rocks and their classification; Weathering — Mechanical and Chemical, Earthquakes and Volcanoes.',
      'Ocean Currents and Tides.',
      'Atmosphere and its composition; Temperature and Atmospheric Pressure, Planetary Winds, Cyclones and Anti-cyclones; Humidity; Condensation and Precipitation; Types of Climate, Major Natural regions of the World.',
      'Regional Geography of India — Climate, Natural vegetation. Mineral and Power resources; location and distribution of agricultural and Industrial activities.',
      'Important Sea ports and main sea, land and air routes of India. Main items of Imports and Exports of India.',
    ],
  },
  {
    id: 'F', name: 'Current Events', weightage: 10, color: '#EC4899', Icon: Newspaper,
    points: [
      'Knowledge of Important events that have happened in India in the recent years.',
      'Current important world events.',
      'Prominent personalities — both Indian and International including those connected with cultural activities and sports.',
    ],
  },
];

/* ── small helpers ─────────────────────────────────────────────────────── */
function AccordionItem({ title, subtitle, points, color, isOpen, onToggle, badge }) {
  return (
    <div className="rounded-2xl border bg-white overflow-hidden transition-shadow hover:shadow-md"
      style={{ borderColor: isOpen ? color + '40' : '#F1F5F9' }}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-5 py-4 cursor-pointer border-none bg-transparent text-left"
      >
        <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 text-xs font-extrabold"
          style={{ background: color + '18', color }}>
          {subtitle}
        </div>
        <span className="flex-1 font-bold text-sm text-slate-800">{title}</span>
        {badge && (
          <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full shrink-0"
            style={{ background: color + '18', color }}>
            {badge}% wt.
          </span>
        )}
        <ChevronDown size={16} className="text-slate-400 shrink-0 transition-transform"
          style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0)' }} />
      </button>
      {isOpen && (
        <div className="px-5 pb-5 border-t" style={{ borderColor: color + '20' }}>
          <div className="mt-3 flex flex-col gap-2">
            {points.map((p, i) => (
              <div key={i} className="flex gap-3">
                <div className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5" style={{ background: color }} />
                <p className="text-sm text-slate-600 leading-relaxed">{p}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── tabs ──────────────────────────────────────────────────────────────── */
const TABS = [
  { id: 'pattern', label: 'Exam Pattern', Icon: Target },
  { id: 'math',    label: 'Paper I – Math', Icon: Calculator },
  { id: 'gat',     label: 'Paper II – GAT', Icon: Globe },
];

/* ════════════════════════════════════════════════════════════════════════
   MAIN PAGE
   ════════════════════════════════════════════════════════════════════════ */
export default function NDASyllabusPage() {
  const [tab, setTab]         = useState('pattern');
  const [openMath, setOpenMath] = useState(null);
  const [openGK, setOpenGK]   = useState(null);

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead
        title="NDA Syllabus 2026 – Complete Paper I (Maths) & Paper II (GAT) Topics"
        description="Complete NDA 2026 syllabus from official UPSC notification. Paper I Mathematics (300 marks): Algebra, Trigonometry, Calculus, Vectors, Statistics. Paper II GAT (600 marks): English (200), Physics, Chemistry, History, Geography & Current Events. Free topic-wise breakdown."
        keywords="NDA syllabus 2026, NDA 2 syllabus, NDA maths syllabus, NDA GAT syllabus, NDA paper 1 syllabus, NDA paper 2 syllabus, UPSC NDA syllabus, NDA exam pattern 2026, NDA marking scheme"
        url="/syllabus/nda"
        jsonLd={NDA_JSON_LD}
      />

      {/* ── Hero ── */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg,#0F172A 0%,#1E1B4B 55%,#1a3a1a 100%)' }}>
        <div className="absolute -top-10 -right-10 w-52 h-52 rounded-full opacity-10" style={{ background: 'radial-gradient(circle,#818CF8,transparent)' }} />
        <div className="absolute bottom-0 left-10 w-40 h-40 rounded-full opacity-10" style={{ background: 'radial-gradient(circle,#34D399,transparent)' }} />

        <div className="w-full px-6 pt-8 pb-6 relative">
          {/* breadcrumb */}
          <div className="flex items-center gap-2 mb-4">
            <Shield size={13} className="text-indigo-400" />
            <span className="text-xs text-indigo-300 font-medium">Defence Exams</span>
            <span className="text-white/20 text-xs">›</span>
            <span className="text-xs text-white/50">NDA Syllabus 2026</span>
          </div>

          <h1 className="font-extrabold text-2xl text-white leading-tight mb-1">NDA Syllabus 2026</h1>
          <p className="text-sm text-indigo-200 mb-5">National Defence Academy & Naval Academy Examination II · UPSC Official Syllabus</p>

          {/* stat chips */}
          <div className="flex flex-wrap gap-2">
            {[
              { label: 'Written Marks', value: '900', color: '#A5B4FC' },
              { label: 'SSB / Interview', value: '900', color: '#6EE7B7' },
              { label: 'Total', value: '1800', color: '#FCD34D' },
              { label: 'Papers', value: '2', color: '#F9A8D4' },
              { label: 'Duration', value: '5 hrs', color: '#93C5FD' },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.08)' }}>
                <span className="text-xs font-extrabold" style={{ color: s.color }}>{s.value}</span>
                <span className="text-[10px] text-white/40">{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* tab bar */}
        <div className="w-full px-6 pb-0">
          <div className="flex gap-1 border-t border-white/10 pt-1">
            {TABS.map(({ id, label, Icon }) => (
              <button key={id} onClick={() => setTab(id)}
                className="flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-bold cursor-pointer border-none transition-all relative"
                style={{ background: 'transparent', color: tab === id ? '#fff' : 'rgba(255,255,255,0.38)' }}>
                <Icon size={13} />
                {label}
                {tab === id && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-10 h-0.5 rounded-full"
                    style={{ background: 'linear-gradient(90deg,#6366F1,#10B981)' }} />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="w-full px-6 py-6 pb-16">

        {/* ══ EXAM PATTERN ══ */}
        {tab === 'pattern' && (
          <div className="flex flex-col gap-5">
            <div>
              <h2 className="font-bold text-base text-slate-800">Scheme of Examination</h2>
              <p className="text-xs text-slate-400 mt-0.5">Written test + SSB Interview — both count equally</p>
            </div>

            {/* paper cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {PAPERS.map(p => (
                <div key={p.code} className="rounded-2xl border bg-white p-5 shadow-sm"
                  style={{ borderTop: `4px solid ${p.color}` }}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <span className="text-[10px] font-extrabold uppercase tracking-widest"
                        style={{ color: p.color }}>Code {p.code}</span>
                      <h3 className="font-extrabold text-base text-slate-800 mt-0.5">{p.name}</h3>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-extrabold" style={{ color: p.color }}>{p.marks}</p>
                      <p className="text-[10px] text-slate-400 font-medium">marks</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Clock size={12} className="text-slate-300" />
                    {p.duration}
                  </div>
                </div>
              ))}
            </div>

            {/* totals */}
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm text-center">
                <p className="text-3xl font-extrabold text-slate-800">900</p>
                <p className="text-xs text-slate-400 mt-1 font-medium">Written Test Total</p>
              </div>
              <div className="rounded-2xl border border-amber-100 bg-amber-50 p-5 shadow-sm text-center">
                <p className="text-3xl font-extrabold text-amber-600">900</p>
                <p className="text-xs text-amber-400 mt-1 font-medium">SSB / Interview</p>
              </div>
            </div>

            {/* key rules */}
            <div>
              <h3 className="font-bold text-sm text-slate-700 mb-3">Key Instructions</h3>
              <div className="flex flex-col gap-2.5">
                {[
                  'All papers consist of Objective Type (MCQ) questions only.',
                  'Question papers of Mathematics and Part "B" of GAT will be set bilingually in Hindi as well as English.',
                  'Questions involving the metric system of Weights and Measures only will be set wherever necessary.',
                  'Candidates must write the papers in their own hand — use of a scribe is not permitted.',
                  'The Commission has discretion to fix qualifying marks in any or all subjects.',
                  'Calculators, mathematical or logarithmic tables are NOT permitted inside the Examination Hall.',
                ].map((rule, i) => (
                  <div key={i} className="flex gap-3 bg-white border border-slate-100 rounded-2xl px-4 py-3 shadow-sm">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-extrabold shrink-0 mt-0.5"
                      style={{ background: '#EEF2FF', color: '#6366F1' }}>{i + 1}</div>
                    <p className="text-sm text-slate-600 leading-relaxed">{rule}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* GK weightage note */}
            <div className="rounded-2xl border border-indigo-100 bg-indigo-50 p-4">
              <p className="text-xs font-bold text-indigo-700 mb-2">📊 Part B (GK) Section Weightage</p>
              <div className="flex flex-wrap gap-2">
                {GK_SECTIONS.map(s => (
                  <div key={s.id} className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl"
                    style={{ background: s.color + '18' }}>
                    <span className="text-[10px] font-extrabold" style={{ color: s.color }}>{s.weightage}%</span>
                    <span className="text-[10px] text-slate-500">{s.name}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-indigo-400 mt-2">A, B, C, D, E, F carry 25%, 15%, 10%, 20%, 20% and 10% weightages respectively.</p>
            </div>
          </div>
        )}

        {/* ══ PAPER I — MATHEMATICS ══ */}
        {tab === 'math' && (
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-bold text-base text-slate-800">Paper I — Mathematics</h2>
                <p className="text-xs text-slate-400 mt-0.5">Code 01 · 300 Marks · 2½ Hours · 8 Units</p>
              </div>
              <button
                onClick={() => setOpenMath(openMath === 'all' ? null : 'all')}
                className="text-xs font-bold text-indigo-500 px-3 py-1.5 rounded-xl bg-indigo-50 border-none cursor-pointer">
                {openMath === 'all' ? 'Collapse all' : 'Expand all'}
              </button>
            </div>

            {MATH_TOPICS.map(topic => (
              <AccordionItem
                key={topic.id}
                title={topic.title}
                subtitle={topic.id}
                points={topic.points}
                color={topic.color}
                isOpen={openMath === topic.id || openMath === 'all'}
                onToggle={() => setOpenMath(prev => (prev === topic.id ? null : topic.id))}
              />
            ))}
          </div>
        )}

        {/* ══ PAPER II — GAT ══ */}
        {tab === 'gat' && (
          <div className="flex flex-col gap-5">
            <div>
              <h2 className="font-bold text-base text-slate-800">Paper II — General Ability Test</h2>
              <p className="text-xs text-slate-400 mt-0.5">Code 02 · 600 Marks · 2½ Hours</p>
            </div>

            {/* Part A — English */}
            <div className="rounded-2xl border bg-white shadow-sm overflow-hidden"
              style={{ borderTop: '4px solid #10B981' }}>
              <div className="px-5 py-4">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-xl flex items-center justify-center"
                      style={{ background: '#ECFDF5' }}>
                      <Languages size={14} className="text-emerald-500" />
                    </div>
                    <div>
                      <p className="text-[10px] font-extrabold uppercase tracking-widest text-emerald-500">Part A</p>
                      <h3 className="font-extrabold text-sm text-slate-800">English</h3>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-extrabold text-emerald-600">200</p>
                    <p className="text-[10px] text-slate-400">marks</p>
                  </div>
                </div>
                <div className="mt-3 flex flex-col gap-2">
                  {[
                    'The question paper in English will be designed to test the candidate\'s understanding of English and workmanlike use of words.',
                    'Grammar and usage.',
                    'Vocabulary.',
                    'Comprehension and cohesion in extended text.',
                    'Overall proficiency in English language.',
                  ].map((p, i) => (
                    <div key={i} className="flex gap-2.5">
                      <div className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5 bg-emerald-400" />
                      <p className="text-sm text-slate-600 leading-relaxed">{p}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Part B — General Knowledge */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <BookOpen size={15} className="text-amber-500" />
                    <h3 className="font-bold text-sm text-slate-800">Part B — General Knowledge</h3>
                    <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-100">400 Marks</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5 ml-5">6 Sections — Physics, Chemistry, General Science, History, Geography, Current Events</p>
                </div>
                <button
                  onClick={() => setOpenGK(openGK === 'all' ? null : 'all')}
                  className="text-xs font-bold text-amber-600 px-3 py-1.5 rounded-xl bg-amber-50 border-none cursor-pointer shrink-0">
                  {openGK === 'all' ? 'Collapse' : 'Expand all'}
                </button>
              </div>

              <div className="flex flex-col gap-3">
                {GK_SECTIONS.map(section => {
                  const { Icon } = section;
                  return (
                    <div key={section.id} className="rounded-2xl border bg-white overflow-hidden transition-shadow hover:shadow-md"
                      style={{ borderColor: (openGK === section.id || openGK === 'all') ? section.color + '40' : '#F1F5F9' }}>
                      <button
                        onClick={() => setOpenGK(prev => (prev === section.id ? null : section.id))}
                        className="w-full flex items-center gap-3 px-5 py-4 cursor-pointer border-none bg-transparent text-left">
                        <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
                          style={{ background: section.color + '18' }}>
                          <Icon size={15} style={{ color: section.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] font-extrabold uppercase tracking-widest" style={{ color: section.color }}>Section {section.id}</span>
                          </div>
                          <p className="font-bold text-sm text-slate-800">{section.name}</p>
                        </div>
                        <span className="text-[10px] font-extrabold px-2 py-1 rounded-full shrink-0"
                          style={{ background: section.color + '18', color: section.color }}>
                          {section.weightage}% wt.
                        </span>
                        <ChevronDown size={15} className="text-slate-300 shrink-0 transition-transform"
                          style={{ transform: (openGK === section.id || openGK === 'all') ? 'rotate(180deg)' : 'rotate(0)' }} />
                      </button>
                      {(openGK === section.id || openGK === 'all') && (
                        <div className="px-5 pb-5 border-t" style={{ borderColor: section.color + '20' }}>
                          <div className="mt-3 flex flex-col gap-2">
                            {section.points.map((p, i) => (
                              <div key={i} className="flex gap-3">
                                <div className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5" style={{ background: section.color }} />
                                <p className="text-sm text-slate-600 leading-relaxed">{p}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* weightage note */}
              <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                <p className="text-xs font-bold text-slate-500 mb-2">Section Weightage Note</p>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Out of maximum marks assigned to Part 'B', questions on Sections 'A', 'B', 'C', 'D', 'E' and 'F' will carry approximately <strong>25%, 15%, 10%, 20%, 20%</strong> and <strong>10%</strong> weightages respectively.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
